/**
 * Subscribe & Save enhancement (§6.2 / §8.3 custom build).
 *
 * Prestige's native "payment_terms" block already renders Shopify's real
 * subscription selector (via the platform's `payment_terms` filter) — this
 * only adds the two behaviors that filter doesn't provide out of the box:
 * pre-selecting the subscription term by default, and showing the savings
 * as a dollar amount instead of Shopify's default percentage copy.
 *
 * IMPORTANT — verified against the live store (see build notes): the spec's
 * assumption that the selling plan lives on the Single variant is wrong; it's
 * currently on the 2-Pack variant, and Single/3-Pack have none at all. Rather
 * than hardcode a variant, this scopes to whatever the platform actually
 * renders — the only structurally guaranteed detail of the native widget is
 * `input[name="selling_plan"]` (the field Shopify submits to /cart/add), so
 * it no-ops cleanly on any variant that has no selling plan.
 *
 * NOTE 2026-08-23: this native block is currently DEAD (renders empty) now
 * that the underlying selling plan is owned by the Shopify Subscriptions
 * app rather than a plain merchant-created plan — see enhanceSubscriptionsAppBlock()
 * below for the replacement that targets the app's own block instead. Left
 * in place (harmless no-op) in case the native filter ever renders content
 * again.
 *
 * NOTE 2026-08-24: the whole file is wrapped in an IIFE. Prestige's pack-size
 * / variant switcher re-fetches the section over the Section Rendering API
 * and re-injects this file's <script> tag into the live DOM on every switch
 * (confirmed live via console — it doesn't just re-run inline code, the tag
 * itself gets reinserted). Top-level `function`/`const` declarations would
 * collide with the previous injection's still-live declarations and throw
 * "Identifier ... has already been declared", aborting the whole script on
 * every variant change. Wrapping in an IIFE gives each injection its own
 * scope so re-injection is a harmless no-op re-registration instead of a
 * crash. (The first injection's event listeners are already bound to
 * `document`/the persistent `.product-info__block-list` node, so they keep
 * working across variant changes even though the second injection's own
 * top-level code — including its own listener registration — never runs;
 * that's fine, it would just be a duplicate registration anyway.)
 */
(function () {
  function enhancePaymentTerms(root) {
    const radios = Array.from(root.querySelectorAll('input[name="selling_plan"]'));
    if (radios.length === 0) return;

    const subscriptionRadio = radios.find((radio) => radio.value !== '');
    if (!subscriptionRadio) return;

    // Default-select Subscribe & Save on load, per §6.2.
    if (!subscriptionRadio.checked) {
      subscriptionRadio.checked = true;
      subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const label = subscriptionRadio.closest('label') || subscriptionRadio.parentElement;
    if (!label || label.dataset.pdpSavingsApplied) return;

    const percentMatch = label.textContent.match(/(\d+(?:\.\d+)?)\s*%/);
    if (!percentMatch) return;
    const percentOff = parseFloat(percentMatch[1]);

    const priceEl = document.querySelector('.product-info sale-price');
    const priceText = priceEl?.textContent.replace(/[^0-9.]/g, '');
    const currentPrice = parseFloat(priceText);
    if (!currentPrice || Number.isNaN(currentPrice)) return;

    const discountedPrice = currentPrice * (1 - percentOff / 100);
    const savings = currentPrice - discountedPrice;
    const currency = priceEl.textContent.trim().match(/^\D+/)?.[0] || '$';

    const format = (value) => `${currency}${value.toFixed(2)}`;

    label.dataset.pdpSavingsApplied = 'true';
    const textNode = Array.from(label.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    const savingsSpan = document.createElement('span');
    savingsSpan.className = 'pdp-savings-override';
    savingsSpan.textContent = ` Subscribe & Save — ${format(discountedPrice)} (Save ${format(savings)})`;

    if (textNode) {
      textNode.textContent = '';
    }
    label.appendChild(savingsSpan);
  }

  function initSubscribeAndSave() {
    document.querySelectorAll('.payment-terms').forEach((root) => enhancePaymentTerms(root));
  }

  document.addEventListener('DOMContentLoaded', initSubscribeAndSave);

  // Re-run whenever the variant changes and the product form section re-renders
  // (Prestige swaps the price/payment-terms markup client-side on variant change).
  document.addEventListener('variant:change', initSubscribeAndSave);
  document.addEventListener('cart:change', initSubscribeAndSave);

  /**
   * Subscribe & Save — Shopify Subscriptions app block support.
   * (Added 2026-08-23, when Emily installed the Shopify Subscriptions app and
   * added its native theme app block to the PDP via the Theme Editor.)
   *
   * The original `enhancePaymentTerms()` above only targets `.payment-terms`
   * — Prestige's native `payment_terms` filter/block. That block is now dead
   * (the selling plan group it would have rendered is owned by the
   * Subscriptions app, and without the app's own block on the page it renders
   * completely empty — see the process doc for the full root-cause writeup).
   * The app's own block renders a real widget instead, with its own markup —
   * `input[type="radio"][name^="purchaseOption_..."]`, not
   * `input[name="selling_plan"]` — so it needs its own pre-select logic here.
   *
   * This only handles pre-selection (§6.2's "Subscribe & Save is pre-checked
   * on page load" requirement). The dollar-savings copy override
   * (`enhancePaymentTerms`'s `pdp-savings-override` span) is NOT reproduced
   * here — that logic depends on the old block's specific label markup, and
   * the app block already shows its own price/discount copy in its own UI,
   * which isn't safely rewritable from outside without risking breaking the
   * app's own event wiring. Flagged separately as a possible follow-up.
   */
  function enhanceSubscriptionsAppBlock() {
    const radioGroups = new Map();

    document.querySelectorAll('input[type="radio"][name^="purchaseOption_"]').forEach((radio) => {
      if (!radioGroups.has(radio.name)) radioGroups.set(radio.name, []);
      radioGroups.get(radio.name).push(radio);
    });

    radioGroups.forEach((radios, name) => {
      if (radios.length < 2) return; // need at least one-time + one subscription option
      if (radios.some((r) => r.checked)) {
        const anyChecked = radios.find((r) => r.checked);
        if (anyChecked.dataset.pdpAutoSelected === 'true' || radios.some((r) => r.dataset.pdpAutoSelectDone === 'true')) {
          return; // already handled this group (either we selected it, or the shopper made their own choice since)
        }
      }

      // Identify the "one-time purchase" option by its nearby text, and treat
      // the other option(s) as the subscription option(s) — more robust than
      // assuming position, in case the app ever reorders its own options.
      const isOneTime = (radio) => {
        const container = radio.closest('label') || radio.closest('[class]') || radio.parentElement;
        return /one[\s-]?time/i.test(container ? container.textContent : '');
      };

      const subscriptionRadio = radios.find((r) => !isOneTime(r));
      if (!subscriptionRadio || subscriptionRadio.checked) {
        radios.forEach((r) => { r.dataset.pdpAutoSelectDone = 'true'; });
        return;
      }

      // Use a real click (not just setting .checked) so the app's own event
      // listeners run and its hidden selling_plan field actually syncs.
      subscriptionRadio.click();
      subscriptionRadio.dataset.pdpAutoSelected = 'true';
      radios.forEach((r) => { r.dataset.pdpAutoSelectDone = 'true'; });
    });
  }

  document.addEventListener('DOMContentLoaded', enhanceSubscriptionsAppBlock);
  document.addEventListener('variant:change', enhanceSubscriptionsAppBlock);

  // The app block renders its widget asynchronously after the page loads —
  // a MutationObserver is the only reliable way to catch the moment its
  // radios actually appear (same reasoning as purchase-panel-layout.js's own
  // observer, for the same kind of async re-render).
  const pdpBlockList = document.querySelector('.shopify-section--main-product .product-info__block-list');
  if (pdpBlockList) {
    new MutationObserver(enhanceSubscriptionsAppBlock).observe(pdpBlockList, { childList: true, subtree: true });
  }
})();
